import * as Sentry from '@sentry/browser';
import { isEnvironmentWithoutPII } from 'hellospa/ts-utils';
import { findDifferences, sortedStringify } from 'hellospa/page/editor/sorted-stringify';
import { identity } from 'lodash';

async function verifyResponses<T>(
  newEndpointPromise: Promise<T>,
  oldEndpointPromise: Promise<T>,
  normalize: (data: [T, T]) => [T, T] = identity,
): Promise<boolean> {
  if (process.env.IS_STORYBOOK) {
    // The data definitely won't match in Storybook so this process needs to be skipped
    return true;
  }

  const [newEndpoint, oldEndpoint] = normalize(await Promise.all([
    newEndpointPromise,
    oldEndpointPromise,
  ]));


  const newEndpointString = sortedStringify(newEndpoint);
  const oldEndpointString = sortedStringify(oldEndpoint);

  if (newEndpointString !== oldEndpointString) {
    const differences = findDifferences(newEndpoint, oldEndpoint);
    if (isEnvironmentWithoutPII() && differences.length !== 0) {
      /* eslint-disable no-console */
      console.error('newEndpoint (after transform)', newEndpoint);
      console.error('was expected to match oldEndpoint', oldEndpoint);
      console.error(differences.join('\n'));
      /* eslint-enable no-console */
    }
    return differences.length === 0;
  }
  return true;
}

export async function loadDataWithFallback<T, Args extends unknown[]>(
  fallbackName: string,
  newEndpoint: (...args: Args) => Promise<T>,
  oldEndpoint: (...args: Args) => Promise<T>,
  args: Args,
  normalizeForComparison: (data: [T, T]) => [T, T] = identity,
) {
  let loadDataPromise;
  try {
    // I'm saving the promise here instead of await(ing) on it so the
    // verification code can fetch the other endpoint in parallel
    const dataPromise = newEndpoint(...args);

    if (isEnvironmentWithoutPII()) {
      loadDataPromise = oldEndpoint(...args);

      if (!await verifyResponses(dataPromise, loadDataPromise, normalizeForComparison)) {
        throw new Error('Endpoint mismatch');
      }
    }
    // This needs to await so that errors are caught in this block.
    const data = await dataPromise;
    return data;
  } catch (error) {
    // Also log this in development
    // eslint-disable-next-line no-console
    console.error(error);
    Sentry.captureException(error);
    // fall through to the return
  }

  Sentry.captureEvent({
    message: `fallback ${fallbackName} was triggered`,
  });

  return (
    // If the old endpoint was fetched for verifiation purposes, use it as the fallback
    loadDataPromise
    || oldEndpoint(...args)
  );

}
